import React, { useState, useEffect, ChangeEvent, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import { getProjects, IProjectRow } from "../../../../../webapi/service/getProjects";
import { Button } from "react-bootstrap";
import "../../../../main.css";
import "./fkdppsettings.css";
import "../../projectdetail.css";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import AuthService from "../../../../../common/AuthService";
import { ChartDialogConfig, ListConfig, ProjectStatus  } from "../../../../../common/constants";
import { getModelEvaluationLists, IModelEvaluationListsRow } from "../../../../../webapi/service/getModelEvaluationLists";
import { getFkdppconf } from "../../../../../webapi/service/getFkdppconf";
import { simulateModel } from "../../../../../webapi/service/simulateModel";
import { getModelSimulationResult } from "../../../../../webapi/service/getModelSimulationResult";
import { IProtData } from "../../../../../webapi/service/getFIle";
import { ModelConstants } from "./constants";
import TrimmingChartDialog from "../file/TrimmingChartDialog";
import BackButton from "../../../common/BackButton";
import { truncation } from "./truncation";

export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * プロジェクト一覧画面に戻る場合に呼び出します
   */
  onTop: () => void;
  /**
   * ファイル一覧をロードする場合に呼び出します
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoadFiles: (loading: boolean) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (param: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

export interface IFkdppConf {
  /**
   * モデルID
   */
  modelid: string;
  /**
   * タグ一覧
   */
  pvtag: string[];
  /**
   * PID制御周期[msec]（学習設定がPID間接制御の場合のみ）
   */
  pidinterval?: number;
  /**
   * FKDPP制御周期[msec]
   */
  fkdppinterval: number;
}

export default function FkdppSimulationContents(props: IProps): JSX.Element {
  const StatusDoing = ProjectStatus.DOING;
  const StatusDone = ProjectStatus.DONE;
  const dialog_area_correction = ChartDialogConfig.CHAERT_DIALOG_AREA_CORRECTION;
  const pv_max = ModelConstants.PIDPV_MAX;
  const pv_min = ModelConstants.PIDPV_MIN;
  const step_max = ModelConstants.PIDSTEP_MAX;
  const step_min = ModelConstants.PIDSTEP_MIN;
  const step_init = ModelConstants.PIDSTEP_INIT;
  const spmax = ModelConstants.SP_MAX;
  const spmin = ModelConstants.SP_MIN;
  const pbmax = ModelConstants.PB_MAX;
  const pbmin = ModelConstants.PB_MIN;
  const pbinit = ModelConstants.PB_INIT;
  const timax = ModelConstants.TI_MAX;
  const timin = ModelConstants.TI_MIN;
  const tiinit = ModelConstants.TI_INIT;
  const tdmax = ModelConstants.TD_MAX;
  const tdmin = ModelConstants.TD_MIN;
  const tdinit = ModelConstants.TD_INIT;
  const rangemax_max = ModelConstants.RANGEMAX_MAX;
  const rangemax_min = ModelConstants.RANGEMAX_MIN;
  const rangemin_max = ModelConstants.RANGEMIN_MAX;
  const rangemin_min = ModelConstants.RANGEMIN_MIN;
  const time_max = ModelConstants.TIME_MAX;
  const time_min = ModelConstants.TIME_MIN;
  const time_init = ModelConstants.TIME_INIT;
  const MenuProps = ListConfig.MENU_PROPS;

  const {
    authService,
    projectInfo,
    onTop,
    onBack,
    onLoading,
    showMessage,
    showConfirm,
    onCommunicationError,
    resetUnoperatedTimeout,
  } = props;
  const projectid = projectInfo.projectid;
  const { t } = useTranslation();

  const [modelinfo, setModelinfo] = useState<IModelEvaluationListsRow[]>([]);
  const [fkdppsimsts, setFkdppsimsts] = useState<string>("");
  const [pvt, setPvt] = useState<IFkdppConf[]>([]);
  const [btnVisible, setBtnVisible] = useState<boolean>(false);
  const [resultProt, setResultProt] = useState<IProtData|undefined>(undefined);
  const [showResultDialog, setShowResultDialog] = useState<boolean>(false);
  const [width, setWidth] = useState<number>(window.innerWidth * dialog_area_correction);
  const [height, setHeight] = useState<number>(window.innerHeight * dialog_area_correction);

  function handleResize() {
    const { innerWidth, innerHeight } = window;
      setWidth(innerWidth * dialog_area_correction);
      setHeight(innerHeight * dialog_area_correction);
  }

  useEffect(() => {
    loadModels();
    loadProjectsts();
    setInitialValue();
    window.addEventListener("resize", handleResize);
  
    return () => {
      window.removeEventListener("resize", handleResize); 
  }}, []); 

  //モデルを取得する
  function loadModels() {
    onLoading(true);
    resetUnoperatedTimeout();
    getModelEvaluationLists({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        if (rows.length === 0) {
          showMessage({
            title: t("fkdpp_simulation_nomodel"),
            message: t("fkdpp_simulation_nomodel_message"),
            onClose:() => onBack(),
          });
        } else {
          setModelinfo(rows);
          loadFkdppconf(rows);
        }        
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);        
      },
    });
  }

  //プロジェクト情報を取得し各種ステータスをセットする
  function loadProjectsts() {
    onLoading(true);
    resetUnoperatedTimeout();
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setFkdppsimsts(row.fkdppsimsts);
        setTimeout(() => {
          onLoading(false);
        });
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      },
    });
  }

  /**
   * モデル学習時の設定を取得し、モデルIDも要素に加える
   * @function loadFkdppconf
   * @param {IModelEvaluationListsRow[]} rows - モデル学習時の設定
   */
  function loadFkdppconf(rows: IModelEvaluationListsRow[]) {
    onLoading(true);
    resetUnoperatedTimeout();
    const idtaglist:IFkdppConf[] = [];
    rows.map((row) => {
      getFkdppconf({
        authService: authService,
        projectid: projectid,
        modelid: row.modelid,
        success: (tags) => {
          const list: IFkdppConf = {
            modelid: row.modelid,
            pvtag: tags.pvtag,
            pidinterval: tags.pidinterval,
            fkdppinterval: tags.fkdppinterval,
          };
          idtaglist.push(list);
        },
        fail: (id, status) => {
          onLoading(false);
          onCommunicationError(id, status);
        }
      });      
    });
    setPvt(idtaglist);
    onLoading(false);   
  }

  //モデルのリストを生成する
  const modelList = modelinfo.map((value) => {
    return (
      <MenuItem key={value.id} value={value.modelid}>
        {value.name}
      </MenuItem>
    );
  });

  //シミュレーション実行
  function handleSimulateModel() {
    resetUnoperatedTimeout(); 
    simulateModel({
      authService: authService,
      projectid: projectid,
      modelid: modelid,
      pvtag: pvtag,
      switchstep: switchstep,
      switchpv: switchpv,
      sp: sp,
      pb: pb,
      ti: ti,
      td: td,
      rangemax: rangemax,
      rangemin: rangemin,
      simstep: simstep,
      success: () => {
        onTop();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      }
    });
  }

  //シミュレーション状況の確認
  function handleCheckSimstatus() {
    onLoading(true);
    resetUnoperatedTimeout();
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setFkdppsimsts(row.fkdppsimsts);
        setTimeout(() => {
          onLoading(false);
          if (row.fkdppsimsts === StatusDoing) {
            showMessage({
              title: t("fkdpp_simulation_doing"),
              message: t("fkdpp_simulation_doing_message")
            });
          } else if (row.simcreatests === StatusDoing) {
            showMessage({
              title: t("simcreatests_doing"),
              message: t("simcreatests_doing_message"),
            });
          } else if (row.simevalsts === StatusDoing) {
            showMessage({
              title: t("simevalsts_doing"),
              message: t("simevalsts_doing_message"),
            });
          } else if (row.fkdpplearnsts === StatusDoing) {
            showMessage({
              title: t("fkdpplearnsts_doing"),
              message: t("fkdpplearnsts_doing_message"),
            });
          } else if (row.fkdppevalsts === StatusDoing) {
            showMessage({
              title: t("fkdppevalsts_doing"),
              message: t("fkdppevalsts_doing_message"),
            });
          } else if (row.fkdppsimsts === StatusDone) {
            showConfirm({
              title: t("fkdpp_simulation_done"),
              message: t("fkdpp_simulation_done_message"),
              onOk:() => handleSimulateModel(),
            });
          } else {
            handleSimulateModel();
          }
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status)
      },
    });
  }

  //結果の表示
  function handleShowResult() {
    resetUnoperatedTimeout();
    onLoading(true);
    getModelSimulationResult({
      authService: authService,
      projectid: projectid,
      success: (data) => {
        const prot: IProtData = {
          fileid: modelid,
          fname: data.id.toString(),
          datanum: data.datanum,
          interval: data.interval,
          input: data.outrows,
          output: data.pvrows
        };
        setResultProt(prot);
        setShowResultDialog(true);
        onLoading(false);
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      }
    })
  }

  function handleCloseResultDialog() {
    setShowResultDialog(false);
    setResultProt(undefined);
  }

  const dialog: CSSProperties | undefined = showResultDialog
      ? undefined
      : { display: "none" };

  function createResultDialog():JSX.Element {
    let contents = <div />
    contents = (
      <TrimmingChartDialog
        show={showResultDialog}
        width={width}
        height={height}
        edit={false}
        cheight={height * 0.5}
        prot={resultProt}
        onCancel={handleCloseResultDialog}
      />
    );
    return contents;  
  }
  const resultDialog = createResultDialog();

  /**
   * 選択肢の情報
   */
  //シミュレーションするモデルのID
  const [modelid, setModelid] = useState<string>("");
  const handleChangeModelid = (event: SelectChangeEvent) => {
    setModelid(event.target.value as string);   
  }

  //PVタグ
  const [pvtag, setPvtag] = useState<string>("");
  const handleChangePvtag = (event: SelectChangeEvent) => {
    setPvtag(event.target.value as string);
  }

  //PID切り替えタイミング
  //PV
  const [switchpv, setSwitchpv] = useState<number>();
  const [fswitchpv, setFswitchpv] = useState<number|string>();
  const [inputSpvErr, setInputSpvErr] = useState<boolean>(false);
  const handleChangeSwitchpv = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= pv_min && Number(str) <= pv_max) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
        setInputSpvErr(false);
        setFswitchpv(str);
        setSwitchpv(val); 
      } else {
        setInputSpvErr(true);
        setFswitchpv(str);
        setSwitchpv(undefined);
      } 
    } else {
      setInputSpvErr(true);
      setFswitchpv(str);
      setSwitchpv(undefined);
    }
  }
  //ステップ
  const [switchstep, setSwitchstep] = useState<number>();
  const [fswitchstep, setFswitchstep] = useState<number|string>(step_init);
  const [inputSstepErr, setInputSstepErr] = useState<boolean>(false);
  const handleChangeSwitchstep = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= step_min && Number(str) <= step_max) {
      const val = Math.round(Number(str));
        setInputSstepErr(false);
        setFswitchstep(str);
        setSwitchstep(val); 
      } else {
        setInputSstepErr(true);
        setFswitchstep(str);
        setSwitchstep(undefined);
      } 
    } else {
      setInputSstepErr(true);
      setFswitchstep(str);
      setSwitchstep(undefined);
    }
  }

  //目標値
  const [sp, setSp] = useState<number>();
  const [fsp, setFsp] = useState<number|string>();
  const [inputSpErr, setInputSpErr] = useState<boolean>(false);
  const handleChangeSp = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= spmin && Number(str) <= spmax) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
        setInputSpErr(false);
        setFsp(str);
        setSp(val);
      } else {
        setInputSpErr(true);
        setFsp(str);
        setSp(undefined);
      }
    } else {
      setInputSpErr(true);
      setFsp(str);
      setSp(undefined);
    }
  };

  //比例帯[%]
  const [pb, setPb] = useState<number>();
  const [fpb, setFpb] = useState<number|string>(pbinit);
  const [inputPbErr, setInputPbErr] = useState<boolean>(false);
  const handleChangePb = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= pbmin && Number(str) <= pbmax) {
      const val = Math.round(Number(str) * Math.pow(10, 1)) / Math.pow(10, 1);
        setInputPbErr(false);
        setFpb(str);
        setPb(val);
      } else {
        setInputPbErr(true);
        setFpb(str);
        setPb(undefined);
      }
    } else {
      setInputPbErr(true);
      setFpb(str);
      setPb(undefined);
    }
  };

  //積分時間[sec]
  const [ti, setTi] = useState<number>();
  const [fti, setFti] = useState<number|string>(tiinit);
  const [inputTiErr, setInputTiErr] = useState<boolean>(false);
  const handleChangeTi = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= timin && Number(str) <= timax) {
      const val = Math.round(Number(str));
        setInputTiErr(false);
        setFti(str);
        setTi(val);
      } else {
        setInputTiErr(true);
        setFti(str);
        setTi(undefined);
      }
    } else {
      setInputTiErr(true);
      setFti(str);
      setTi(undefined);
    }
  }

  //微分時間[sec]
  const [td, setTd] = useState<number>();
  const [ftd, setFtd] = useState<number|string>(tdinit);
  const [inputTdErr, setInputTdErr] = useState<boolean>(false);
  const handleChangeTd = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= tdmin && Number(str) <= tdmax) {
      const val = Math.round(Number(str));
        setInputTdErr(false);
        setFtd(str);
        setTd(val);
      } else {
        setInputTdErr(true);
        setFtd(str);
        setTd(undefined);
      }
    } else {
      setInputTdErr(true);
      setFtd(str);
      setTd(undefined);
    } 
  }

  //制御レンジ上限
  const [rangemax, setRangemax] = useState<number>();
  const [frangemax, setFrangemax] = useState<number|string>();
  const [inputRmaxErr, setInputRmaxErr] = useState<boolean>(false);
  const handleChangeRangemax = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= rangemax_min && Number(str) <= rangemax_max) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
        setInputRmaxErr(false);
        setFrangemax(str);
        setRangemax(val);
      } else {
        setInputRmaxErr(true);
        setFrangemax(str);
        setRangemax(undefined);
      }
    } else {
      setInputRmaxErr(true);
      setFrangemax(str);
      setRangemax(undefined);
    }
  };

  //制御レンジ下限
  const [rangemin, setRangemin] = useState<number>();
  const [frangemin, setFrangemin] = useState<number|string>();
  const [inputRminErr, setInputRminErr] = useState<boolean>(false);
  const handleChangeRangemin = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= rangemin_min && Number(str) <= rangemin_max) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
        setInputRminErr(false);
        setFrangemin(str);
        setRangemin(val);
      } else {
        setInputRminErr(true);
        setFrangemin(str);
        setRangemin(undefined);
      }
    } else {
      setInputRminErr(true);
      setFrangemin(str);
      setRangemin(undefined);
    }
  };

  //シミュレーション時間
  const [simstep, setSimstep] = useState<number>();
  const [fsimstep, setFsimstep] = useState<number|string>(time_init);
  const [inputSimstepErr, setInputSimstepErr] = useState<boolean>(false);
  const handleChangeSimstep = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= time_min && Number(str) <= time_max) {
      const val = Math.round(Number(str));
        setInputSimstepErr(false);
        setFsimstep(str);
        setSimstep(val);
      } else {
        setInputSimstepErr(true);
        setFsimstep(str);
        setSimstep(undefined);
      }
    } else {
      setInputSimstepErr(true);
      setFsimstep(str);
      setSimstep(undefined);
    } 
  }

  //初期値のある値を設定する
  function setInitialValue() {
    setSwitchstep(Number(fswitchstep));
    setPb(Number(fpb));
    setTi(Number(fti));
    setTd(Number(ftd));
    setSimstep(Number(fsimstep));
  }

  /**
   * ボタン表示切替、制御レンジ上下限値と目標値の大小関係チェック
   */
  useEffect(() => {
    setButtonVisible();
    checkRange();
  }, [
    modelid,
    pvtag,
    switchpv,
    switchstep,
    sp,
    pb,
    ti,
    td,
    rangemax,
    rangemin,
    simstep,
    inputSpvErr,
    inputSstepErr,
    inputSpErr,
    inputPbErr,
    inputTiErr,
    inputTdErr,
    inputRmaxErr,
    inputRminErr,
    inputSimstepErr,
  ]);

  function setButtonVisible() {
    if (
      modelid &&
      pvtag &&
      switchpv !== undefined &&
      switchstep !== undefined &&
      sp !== undefined &&
      pb &&
      ti !== undefined &&
      td !== undefined &&
      rangemax !== undefined &&
      rangemin !== undefined &&
      simstep !== undefined
    ) {
      if (
        !inputSpvErr && !inputSstepErr &&
        !inputSpErr && !inputPbErr && !inputTiErr && !inputTdErr &&
        !inputRmaxErr && !inputRminErr && !inputSimstepErr
      ) {
        setBtnVisible(true);
      } else {
        setBtnVisible(false);
      }      
    } else {
      setBtnVisible(false);
    }
  }

  //制御レンジ上下限値チェック
  function checkRange() {
    if (
      //上限、下限どちらにもエラーが無い場合
      !inputRmaxErr &&
      !inputRminErr &&
      rangemin !== undefined &&
      rangemax !== undefined
    ) {
      if (rangemin >= rangemax) {
        setInputRmaxErr(true);
        setInputRminErr(true);
      } else {
        setInputRmaxErr(false);
        setInputRminErr(false);
        //レンジ上下限値の範囲が正常の場合目標値も確認
        if (sp !== undefined && sp <= rangemax && sp >= rangemin) {
          setInputSpErr(false);
        } else {
          setInputSpErr(true);
        }
      }
    } else if (
      //上限、下限のエラーの有無を問わない場合
      rangemin !== undefined &&
      rangemax !== undefined
    ) {
      if (rangemin >= rangemax) {
        setInputRmaxErr(true);
        setInputRminErr(true);
      } else { 
        if (rangemax >= rangemax_min && rangemax <= rangemax_max) {
          setInputRmaxErr(false);
        }
        if (rangemin >= rangemin_min && rangemin <= rangemin_max) {
          setInputRminErr(false);
        }
        if (sp !== undefined && sp <= rangemax && sp >= rangemin) {
          setInputSpErr(false);
        } else {
          setInputSpErr(true);
        }
      }
    }
  }

  /**
   * 各種入力フォーム
   */
  function createModelForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_model")}</div>
        <FormControl  sx ={{mr: 1 , width: 1 / 2}}>
          <InputLabel id="modelid">{t("fkdpp_simulation_model")}</InputLabel>
            <Select
              labelId="model"
              id="modelid"
              value={modelid}
              label={t("fkdpp_simulation_model")}
              MenuProps={MenuProps}
              onChange={handleChangeModelid}
            >
              {modelList}
            </Select>
        </FormControl>
      </div>
    );
    return contents;
  }
  const modelForm = createModelForm();

  //modelidの情報が変わったらpvタグの選択肢を作り替え、PVタグをリセットする
  useEffect(() => {
    createPvtagList();
    createPidintervalForm();
    setPvtag("");
  }, [modelid]);

  function createPvtagList() {
    if (modelid === "") {
      return <div />;
    } else {
      const selectedModelTag = pvt.find((value) => {
        return value.modelid === modelid;
      });
      const tagList = selectedModelTag?.pvtag.map((tag, i) => {
        return (
          <MenuItem
            key={i}
            value={tag}
          >
            {tag}
          </MenuItem>
        );
      });
      return tagList;
    }    
  }
  const pvTagList = createPvtagList();

  function createIntervalValue() {
    if (modelid === "") {
      return <div />;
    } else {
      const intervalValue = pvt.find((value) => {
        if (value.modelid === modelid) {
          return value.fkdppinterval;
        }      
      });
      return intervalValue?.fkdppinterval;
    }    
  }
  const intervalValue = createIntervalValue();

  function createPvTagForm() {
    let contents = <div />
    contents = (
      <div>
        <div className="contents">
          <div className="contents">{t("fkdpp_simulation_pvtag")}</div>
          <FormControl sx={{ mr: 1, width: 1 / 2 }}>
            <InputLabel id="pvtag">{t("fkdpp_simulation_pvtag")}</InputLabel>
            <Select
              labelId="pvtag"
              id="pvtag"
              value={pvtag}
              label={t("fkdpp_simulation_pvtag")}
              MenuProps={MenuProps}
              onChange={handleChangePvtag}
            >
              {pvTagList}
            </Select>
          </FormControl>
        </div>
        <div className="contents">
          <FormControl sx={{ mt: 2, mr: 5, mb: 2, ml: 1 }}>
            {t("fkdpp_simulation_interval")}
          </FormControl>
          <FormControl sx={{ mt: 2, mr: 1, mb: 2, ml: 1 }}>
            {intervalValue}
          </FormControl>
        </div>
      </div>
    );
    return contents;
  }
  const pvTagForm = modelid === "" ? 
    <FormControl fullWidth sx ={{m:2}}>{t("fkdpp_simulation_emptymodel")}</FormControl> : 
    createPvTagForm();

  function createSwitchForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_timing")}</div>
        <div className="contents">{t("fkdpp_simulation_pvequall")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 4 }}>
          <TextField
            required
            id="switchpv"
            label={t("fkdpp_simulation_switchpv")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={fswitchpv}
            error={inputSpvErr}
            helperText={t("fkdpp_simulation_switchpv_error")}
            onChange={handleChangeSwitchpv}
            onBlur={() => setFswitchpv(truncation(fswitchpv, 5, inputSpvErr))}
          />
        </FormControl>
        <div className="contents">{t("fkdpp_simulation_from")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 4 }}>
          <TextField
            required
            id="switchstep"
            label={t("fkdpp_simulation_switchstep")}
            variant="outlined"
            inputProps={{ maxLength: 5 }}
            value={fswitchstep}
            error={inputSstepErr}
            helperText={t("fkdpp_simulation_switchstep_error")}
            onChange={handleChangeSwitchstep} 
            onBlur={() => setFswitchstep(truncation(fswitchstep, 0, inputSstepErr))}
          />
        </FormControl>
        <div className="contents">{t("fkdpp_simulation_step")}</div>
    </div>
    );
    return contents;
  }
  const switchForm = createSwitchForm();

  const pidTitle = 
    <div className="pid-title">
      <h4>{t("fkdpp_simulation_pidtitle")}</h4>
    </div>

  function createPbForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_pb")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="pb"
            label={t("fkdpp_simulation_pb")}
            variant="outlined"
            inputProps={{ maxLength: 5 }}
            value={fpb}
            error={inputPbErr}
            helperText={t("fkdpp_simulation_pb_error")}
            onChange={handleChangePb} 
            onBlur={() => setFpb(truncation(fpb, 1, inputPbErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const pbForm = createPbForm();

  function createTdForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_td")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="td"
            variant="outlined"
            label={t("fkdpp_simulation_td")}
            inputProps={{ maxLength: 4 }}
            value={ftd}
            error={inputTdErr}
            helperText={t("fkdpp_simulation_td_error")}
            onChange={handleChangeTd}
            onBlur={() => setFtd(truncation(ftd, 0, inputTdErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const tdForm = createTdForm();

  function createTiForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_ti")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="ti"
            variant="outlined"
            label={t("fkdpp_simulation_ti")}
            inputProps={{ maxLength: 4 }}
            value={fti}
            error={inputTiErr}
            helperText={t("fkdpp_simulation_ti_error")}
            onChange={handleChangeTi} 
            onBlur={() => setFti(truncation(fti, 0, inputTiErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const tiForm = createTiForm();

  function createSpForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_sp")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="sp"
            label={t("fkdpp_simulation_sp")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={fsp}
            error={inputSpErr}
            helperText={t("fkdpp_simulation_sp_error")}
            onChange={handleChangeSp}
            onBlur={() => setFsp(truncation(fsp, 5, inputSpErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const spForm = createSpForm();

  /**
   * PID制御周期表示フォームを作成する
   * @returns {JSX.Element} 選択フォーム
   */
  function createPidintervalValue() {
    if (modelid === "") {
      return <div />;
    } else {
      const selected = pvt.find((row) => {
        if (row.modelid === modelid) {
          return row;
        }
      });
      if (selected?.pidinterval) {
        return selected?.pidinterval;
      } else {
        return "Auto";
      }
    }
  }
  const pidIntervalValue = createPidintervalValue();

  function createPidintervalForm(): JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
          <FormControl sx={{ mt: 2, mr: 5, mb: 2, ml: 1 }}>
            {t("fkdpp_simulation_pidinterval")}
          </FormControl>
          <FormControl sx={{ mt: 2, mr: 1, mb: 2, ml: 1 }}>
            {pidIntervalValue}
          </FormControl>
        </div>
    );
    return contents;
  }
  const pidintervalForm = modelid === "" ?
  <FormControl fullWidth sx ={{m:2}}>{t("fkdpp_simulation_emptymodel")}</FormControl> :
  createPidintervalForm();
  
  function createRangemaxForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_rangemax")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="rangemax"
            label={t("fkdpp_simulation_rangemax")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={frangemax}
            error={inputRmaxErr}
            helperText={t("fkdpp_simulation_rangemax_error")}
            onChange={handleChangeRangemax}
            onBlur={() => setFrangemax(truncation(frangemax, 5, inputRmaxErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const rangemaxForm = createRangemaxForm();

  function createRangeminForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_rangemin")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="rangemin"
            label={t("fkdpp_simulation_rangemin")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={frangemin}
            error={inputRminErr}
            helperText={t("fkdpp_simulation_rangemin_error")}
            onChange={handleChangeRangemin}
            onBlur={() => setFrangemin(truncation(frangemin, 5, inputRminErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const rangeminForm = createRangeminForm();

  function createSimstepForm():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_simulation_simstep")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="simstep"
            variant="outlined"
            label={t("fkdpp_simulation_simstep")}
            inputProps={{ maxLength: 5 }}
            value={fsimstep}
            error={inputSimstepErr}
            helperText={t("fkdpp_simulation_simstep_error")}
            onChange={handleChangeSimstep}
            onBlur={() => setFsimstep(truncation(fsimstep, 0, inputSimstepErr))}
          />
        </FormControl>
        <div className="contents">{t("fkdpp_simulation_simstep_step")}</div>
      </div>
    );
    return contents;
  }
  const simstepForm = createSimstepForm();

  function createPidsimButton(): JSX.Element {
    let contents = <div />;
    if (btnVisible === true) {
      contents = (
        <FormControl sx={{ mt: 0, mr: 0, mb: 6, ml: 0 }}>
          <Button
            id="createbtn"
            className="btn-mainspace"
            onClick={() => handleCheckSimstatus()}
          >
            {t("fkdpp_simulation_create")}
          </Button>
        </FormControl>
      );
    } else {
      contents = (
        <FormControl sx={{ mt: 0, mr: 0, mb: 6, ml: 0 }}>
          <Button id="createbtn" className="btn-mainspace" disabled>
            {t("fkdpp_simulation_create")}
          </Button>
        </FormControl>
      );
    }
    return contents;
  }
  const pidsimButton = createPidsimButton();

  function createResultButton():JSX.Element {
    let contents = <div />
    contents = (
      <Button 
        id="resultbtn"
        className="btn-mainspace" 
        onClick={() => handleShowResult()}>
        {t("fkdpp_simulation_result")}
      </Button>
    );
    return contents;
  }
  const resultButton = (fkdppsimsts === StatusDone) ? createResultButton() : "";

  return (
    <div>
      <div className="mainspace-toolbar">
        <BackButton onClick={onBack} />
        {resultButton}
      </div>
      <div className="mainspace-toolbar">
        <h3>{t("fkdpp_simulation_settings")}</h3>
      </div>
      <div className="test-form">
        {modelForm}
        {pvTagForm}
        {switchForm}
        {simstepForm}
        {pidTitle}
        {spForm}
        {pbForm}
        {tiForm}
        {tdForm}
        {pidintervalForm}
        {rangemaxForm}
        {rangeminForm}
        {pidsimButton}
        <div className="overlay" style={dialog}>
          {resultDialog}
        </div>
      </div>
    </div>
  );
}
